import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  MapPin, 
  Bed, 
  Bath, 
  Square, 
  Eye, 
  Heart,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  Building,
  ChevronLeft,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import RentalRequestForm from '@/components/RentalRequestForm';
import PropertyRating from '@/components/PropertyRating';
import { toast } from '@/hooks/use-toast';
import PropertyLocationMap from '@/components/PropertyLocationMap';

interface Property {
  id: string;
  title: string;
  description: string;
  detailed_description?: string;
  address: string;
  monthly_price: number;
  type: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  floor_number?: number;
  building_age?: number;
  furnishing_status: string;
  features: string[];
  available_from: string;
  occupied: boolean;
  views: number;
  is_active: boolean;
  contact_phone?: string;
  contact_email?: string;
  contact_whatsapp?: string;
  location_lat?: number;
  location_lng?: number;
}

interface PropertyImage {
  id: string;
  url: string;
  storage_path?: string;
  image_order: number;
  is_main: boolean;
}

const PropertyDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [images, setImages] = useState<PropertyImage[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isSaved, setIsSaved] = useState(false);

  const { data: property, isLoading, error, refetch } = useQuery({
    queryKey: ['property', id],
    queryFn: async () => {
      if (!id) throw new Error('لا يوجد معرف للعقار');

      console.log('جلب تفاصيل العقار:', id);

      // زيادة عدد المشاهدات
      await supabase.rpc('increment_property_views', { 
        property_uuid: id 
      });

      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('id', id)
        .eq('is_active', true)
        .single();

      if (error) {
        throw error;
      }

      return {
        ...data,
        features: Array.isArray(data.features) 
          ? data.features.filter((f): f is string => typeof f === 'string')
          : [],
        views: data.views || 0
      } as Property;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (property) {
      fetchAllImages();
      checkIfSaved();
    }
  }, [property]);

  const fetchAllImages = async () => {
    if (!property) return;

    try {
      const { data: imagesData, error } = await supabase
        .from('property_images')
        .select('*')
        .eq('property_id', property.id)
        .order('image_order', { ascending: true });

      if (error) {
        return;
      }

      if (imagesData && imagesData.length > 0) {
        const processedImages = imagesData.map(img => {
          let imageUrl: string = '';

          if (img.storage_path && img.storage_path.trim() !== '') {
            const { data } = supabase.storage
              .from('property-images')
              .getPublicUrl(img.storage_path);
            
            imageUrl = data.publicUrl;
          } else if (img.url && !img.url.startsWith('blob:')) {
            imageUrl = img.url;
          }

          return {
            id: img.id,
            url: imageUrl,
            storage_path: img.storage_path,
            image_order: img.image_order,
            is_main: img.is_main
          };
        }).filter(img => img.url);

        setImages(processedImages);
        console.log('الصور المعالجة:', processedImages);
      }
    } catch (error) {
      console.error('خطأ في جلب صور العقار:', error);
    }
  };

  const checkIfSaved = async () => {
    if (!property) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('saved_properties')
        .select('id')
        .eq('user_id', user.id)
        .eq('property_id', property.id)
        .maybeSingle();

      setIsSaved(!!data);
    } catch (error) {
      setIsSaved(false);
    }
  };

  const handleSaveProperty = async () => {
    if (!property) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "تسجيل الدخول مطلوب",
          description: "يجب تسجيل الدخول لحفظ العقار",
          variant: "destructive",
        });
        return;
      }

      if (isSaved) {
        await supabase
          .from('saved_properties')
          .delete()
          .eq('user_id', user.id)
          .eq('property_id', property.id);
        setIsSaved(false);
        toast({
          title: "تم إلغاء الحفظ",
          description: "تم إزالة العقار من المفضلة",
        });
      } else {
        await supabase
          .from('saved_properties')
          .insert({
            user_id: user.id,
            property_id: property.id
          });
        setIsSaved(true);
        toast({
          title: "تم الحفظ",
          description: "تم إضافة العقار إلى المفضلة",
        });
      }
    } catch (error) {
      console.error('خطأ في حفظ العقار:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء حفظ العقار",
        variant: "destructive",
      });
    }
  };

  const handleRequestSuccess = () => {
    setIsDialogOpen(false);
    refetch();
  };

  const handleRatingSuccess = () => {
    refetch();
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const openGoogleMaps = () => {
    if (property?.location_lat && property?.location_lng) {
      const mapsUrl = `https://www.google.com/maps?q=${property.location_lat},${property.location_lng}`;
      window.open(mapsUrl, '_blank');
    }
  };

  const handlePhoneClick = (phone: string) => {
    window.open(`tel:${phone}`, '_self');
  };

  const handleEmailClick = (email: string) => {
    window.open(`mailto:${email}`, '_self');
  };

  const handleWhatsAppClick = (whatsapp: string) => {
    const cleanPhone = whatsapp.replace(/[^\d+]/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">جاري تحميل تفاصيل العقار...</p>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <CardContent>
            <p className="text-red-600 mb-4">لم يتم العثور على العقار أو حدث خطأ في التحميل</p>
            <Button onClick={() => window.history.back()} variant="outline">
              العودة
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* شريط التنقل العلوي */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-20">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
                      الرئيسية
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/properties" className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
                      العقارات
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-gray-700">
                    تفاصيل العقار
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <Button 
              variant="outline" 
              size="sm" 
              asChild
              className="flex items-center gap-2"
            >
              <Link to="/properties">
                <ArrowLeft className="h-4 w-4" />
                العودة للعقارات
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* الصور والمعلومات الأساسية */}
          <div className="lg:col-span-2">
            <Card className="overflow-hidden mb-6">
              {/* معرض الصور */}
              <div className="relative h-80 bg-gray-200 flex items-center justify-center">
                {images.length > 0 ? (
                  <>
                    <img
                      src={images[currentImageIndex].url}
                      alt={`${property.title} - صورة ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* أزرار التنقل بين الصور */}
                    {images.length > 1 && (
                      <>
                        <Button
                          variant="secondary"
                          size="icon"
                          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                          onClick={prevImage}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="icon"
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                          onClick={nextImage}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                        
                        {/* مؤشر الصور */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                          {images.map((_, index) => (
                            <button
                              key={index}
                              className={`w-2 h-2 rounded-full ${
                                index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                              }`}
                              onClick={() => setCurrentImageIndex(index)}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-center text-gray-500">
                    <Building className="h-16 w-16 mx-auto mb-2" />
                    <p>لا توجد صور للعقار</p>
                  </div>
                )}
                
                {/* شارة الحالة */}
                <div className="absolute top-4 left-4">
                  <Badge variant={property.occupied ? "secondary" : "default"}>
                    {property.occupied ? "مؤجر" : "متاح"}
                  </Badge>
                </div>

                {/* عداد الصور */}
                {images.length > 0 && (
                  <div className="absolute top-4 right-16 bg-black/50 text-white px-2 py-1 rounded text-sm">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                )}

                {/* زر الحفظ */}
                <div className="absolute top-4 right-4">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="bg-white/80 hover:bg-white"
                    onClick={handleSaveProperty}
                  >
                    <Heart className={`h-4 w-4 ${isSaved ? 'fill-red-500 text-red-500' : ''}`} />
                  </Button>
                </div>
              </div>

              <CardContent className="p-6">
                {/* العنوان والسعر */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-right mb-2">
                      {property.title}
                    </h1>
                    <div className="flex items-center gap-1 text-gray-600 mb-2">
                      <MapPin className="h-4 w-4" />
                      <span className="text-right">{property.address}</span>
                    </div>
                  </div>
                  <div className="text-left ml-4">
                    <div className="text-3xl font-bold text-green-600">
                      {property.monthly_price} ر.ع
                    </div>
                    <div className="text-sm text-gray-500">شهرياً</div>
                  </div>
                </div>

                {/* النوع وحالة الأثاث */}
                <div className="flex gap-2 mb-4">
                  <Badge variant="outline">{property.type}</Badge>
                  <Badge variant="outline">
                    {property.furnishing_status === 'furnished' ? 'مفروش' : 
                     property.furnishing_status === 'semi_furnished' ? 'مفروش جزئياً' : 'غير مفروش'}
                  </Badge>
                </div>

                {/* المواصفات */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <Bed className="h-5 w-5 text-blue-600" />
                    <div>
                      <div className="text-sm text-gray-600">غرف النوم</div>
                      <div className="font-bold">{property.bedrooms}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <Bath className="h-5 w-5 text-blue-600" />
                    <div>
                      <div className="text-sm text-gray-600">الحمامات</div>
                      <div className="font-bold">{property.bathrooms}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <Square className="h-5 w-5 text-blue-600" />
                    <div>
                      <div className="text-sm text-gray-600">المساحة</div>
                      <div className="font-bold">{property.area} م²</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <Eye className="h-5 w-5 text-blue-600" />
                    <div>
                      <div className="text-sm text-gray-600">المشاهدات</div>
                      <div className="font-bold">{property.views}</div>
                    </div>
                  </div>
                </div>

                {/* الوصف العادي */}
                {property.description && (
                  <div className="mb-6">
                    <h3 className="text-lg font-bold mb-2 text-right">الوصف</h3>
                    <p className="text-gray-700 text-right leading-relaxed">
                      {property.description}
                    </p>
                  </div>
                )}

                {/* الوصف التفصيلي */}
                {property.detailed_description && (
                  <div className="mb-6">
                    <h3 className="text-lg font-bold mb-2 text-right">الوصف التفصيلي</h3>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-gray-700 text-right leading-relaxed whitespace-pre-line">
                        {property.detailed_description}
                      </p>
                    </div>
                  </div>
                )}

                {/* المميزات */}
                {property.features && property.features.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-bold mb-2 text-right">المميزات</h3>
                    <div className="flex flex-wrap gap-2">
                      {property.features.map((feature, index) => (
                        <Badge key={index} variant="secondary">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* معلومات إضافية */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {property.floor_number && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">الطابق:</span>
                      <span className="font-medium">{property.floor_number}</span>
                    </div>
                  )}
                  {property.building_age && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">عمر المبنى:</span>
                      <span className="font-medium">{property.building_age} سنة</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">متاح من:</span>
                    <span className="font-medium">
                      {new Intl.DateTimeFormat('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }).format(new Date(property.available_from))}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* إضافة مكون الموقع الجغرافي الجديد */}
            {(property.location_lat && property.location_lng) && (
              <PropertyLocationMap
                latitude={property.location_lat}
                longitude={property.location_lng}
                address={property.address}
                title={property.title}
              />
            )}
          </div>

          {/* معلومات الاتصال وطلب الإيجار */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-4">
              {/* معلومات الاتصال */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-right">معلومات الاتصال</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {property.contact_phone && (
                    <button
                      onClick={() => handlePhoneClick(property.contact_phone!)}
                      className="w-full flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer"
                    >
                      <Phone className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-blue-600">{property.contact_phone}</span>
                    </button>
                  )}
                  {property.contact_email && (
                    <button
                      onClick={() => handleEmailClick(property.contact_email!)}
                      className="w-full flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer"
                    >
                      <Mail className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-blue-600">{property.contact_email}</span>
                    </button>
                  )}
                  {property.contact_whatsapp && (
                    <button
                      onClick={() => handleWhatsAppClick(property.contact_whatsapp!)}
                      className="w-full flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors cursor-pointer"
                    >
                      <MessageSquare className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-600">{property.contact_whatsapp}</span>
                    </button>
                  )}
                </CardContent>
              </Card>

              {/* طلب الإيجار وتقييم العقار */}
              <Card>
                <CardContent className="p-4 space-y-3">
                  {!property.occupied && (
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full" size="lg">
                          <MessageSquare className="h-4 w-4 mr-2" />
                          طلب إيجار العقار
                        </Button>
                      </DialogTrigger>
                      <DialogContent 
                        className="max-w-2xl max-h-[90vh] overflow-y-auto"
                        aria-describedby="rental-request-description"
                      >
                        <DialogHeader>
                          <DialogTitle className="text-right">طلب إيجار العقار</DialogTitle>
                        </DialogHeader>
                        <div id="rental-request-description" className="sr-only">
                          نموذج لإرسال طلب إيجار للعقار
                        </div>
                        <RentalRequestForm 
                          propertyId={property.id}
                          propertyTitle={property.title}
                          onSuccess={handleRequestSuccess}
                        />
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  {/* زر تقييم العقار */}
                  <PropertyRating
                    propertyId={property.id}
                    propertyTitle={property.title}
                    onSuccess={handleRatingSuccess}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;
